// components/LeftPanel.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate, formatDay } from "utils/helpers";
import FileView from "components/fileView";
import { LoadingIcon } from "utils/constant";
import ShowUI, { CanShow, LEGISLATIVE_DRAFTING_UI_RULES } from "utils/authRules";
import { updateLegislativeDrafting } from "services/legislativeService";
import { useToast } from "contexts/ToastContext";
import { SECTOR_DIRECTIVES_REQUEST_STATUS } from "utils/constantValues";
import HeadRemark from "./headRemark";
import DirectorRemark from "./directorRemark";
import CoordinatorRemark from "./coordinatorRemark";
import ProsecutorRemark from "./prosecutorRemark";
import ActionAvatars from "pages/conductLegalResearch/fragments/actionAvatars";
import PublicFeedback from "./publicFeedback";
import UploadAmendedFile from "./uploadAmendedFile";
import DynamicFormInput from "components/form/form";
import { useAuth } from "contexts/AuthContext";
import { StatusChip } from "components/StatusChip";
import RespondBackForm from "pages/conductLegalResearch/fragments/respondBackform";
import LegislativeRespondBackForm from "./LegislativeResponback";
import { updateLegalAdvisory } from "services/legalAdvisoryService";
import { updateSectorDirective } from "services/sectorDirectives";
import TeamView from "components/team_view";
import DocumentViewerModal from "components/DocumentViewer";
import ConfirmationModal from "components/ConfirmationModal1";
import { useTranslation } from "react-i18next";

// SummaryWrittenCard — shows latest written_comments_file independently,
// previous files in accordion, and allows coordinator to edit when
// forward_darft / forward_darft_to_director / forward_darft_to_head UI rules are active.
function SummaryWrittenCard({ formState, setFormState }: { formState: any; setFormState: any }) {
    const { t } = useTranslation();
    const [editMode, setEditMode] = useState(false);
    const [summary, setSummary] = useState(formState?.summary_of_comments || "");
    const [newFile, setNewFile] = useState<File | null>(null);
    const [saving, setSaving] = useState(false);
    const { showToast } = useToast();
    const contextData = useAuth();
    const isInternal = contextData?.user?.user_type === "internal";

    const writtenFiles = (formState?.uploaded_files || []).filter(
        (document: any) => document.document_type === "written_comments_file"
    );
    if (writtenFiles.length === 0) return null;

    const sorted = [...writtenFiles].sort(
        (a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    const currentFile = sorted[0];
    const previousFiles = sorted.slice(1);

    // Check if any of the coordinator forward_darft UI rules are active
    const coordinatorEditKeys = [
        LEGISLATIVE_DRAFTING_UI_RULES.forward_darft.value,
        LEGISLATIVE_DRAFTING_UI_RULES.forward_darft_to_director.value,
        LEGISLATIVE_DRAFTING_UI_RULES.forward_darft_to_head.value,
    ];

    const handleSave = async () => {
        setSaving(true);
        try {
            const data = new FormData();
            data.append("summary_of_comments", summary);
            if (newFile) data.append("comments_files", newFile);
            const response = await updateSectorDirective(formState.id, data, contextData.token || contextData?.user?.access_token || "");
            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast(t("summary_updated_success") || "Summary updated successfully!", "success");
            setEditMode(false);
            setNewFile(null);
        } catch (error) {
            console.error(error);
            showToast(t("FailedToUpdateSummary") || "Failed to update summary.", "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="flex-1 bg-white mb-4 rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            {/* Official header */}
            <div className="bg-gradient-to-r from-[#0A1D39] to-[#13294d] px-5 py-3 flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                </div>
                <div className="min-w-0 flex-1">
                    <p className="text-amber-300 text-[10px] uppercase tracking-wider font-semibold">
                        {t("OfficeOfTheAttorneyGeneral") || "Office of the Attorney General"}
                    </p>
                    <h3 className="text-white font-bold text-base truncate">{t("SummaryWritten") || "Summary Written"}</h3>
                </div>
                {/* Show edit button only when coordinator forward_darft rules are active */}
                {coordinatorEditKeys.map((key) => (
                    <ShowUI
                        key={key}
                        ui_key={key}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        {!editMode ? (
                            <button
                                onClick={() => setEditMode(true)}
                                className="flex items-center gap-1 text-sm font-medium text-amber-200 hover:text-white"
                                title={t("EditSummary") || "Edit Summary"}
                            >
                                <svg width="16" height="16" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z" fill="currentColor" />
                                </svg>
                                {t("Edit") || "Edit"}
                            </button>
                        ) : (
                            <button
                                onClick={() => { setEditMode(false); setSummary(formState?.summary_of_comments || ""); setNewFile(null); }}
                                className="text-sm text-amber-200 hover:text-white px-2 py-1 rounded"
                            >
                                {t("Cancel") || "Cancel"}
                            </button>
                        )}
                    </ShowUI>
                ))}
            </div>

            <div className="p-5">
            <p className="text-[#747171] font-normal text-xs mb-3">{formatDay(formState?.created_at).rest}</p>

            {/* Edit mode */}
            {editMode ? (
                <div className="space-y-3">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t("SummaryOfComments") || "Summary of Comments"}</label>
                        <textarea
                            className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-800 resize-y min-h-[80px] text-sm"
                            value={summary}
                            onChange={(e) => setSummary(e.target.value)}
                            placeholder={t("EnterSummaryPlaceholder") || "Enter summary..."}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t("ReplaceWrittenCommentsFile") || "Replace Written Comments File (optional)"}</label>
                        <input
                            type="file"
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
                            onChange={(e) => setNewFile(e.target.files?.[0] || null)}
                        />
                        {newFile && <p className="text-xs text-gray-500 mt-1">{t("Selected") || "Selected"}: {newFile.name}</p>}
                    </div>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="w-full bg-[#0A1D39] hover:bg-gray-800 text-white font-semibold py-2 px-4 rounded-md text-sm"
                    >
                        {saving ? (t("Saving") || "Saving...") : (t("SaveChanges") || "Save Changes")}
                    </button>
                </div>
            ) : (
                <>
                    <p className="font-regular text-base text-[#606060] mb-3">{formState?.summary_of_comments}</p>
                    {/* Latest written comments file — shown independently */}
                    <DocumentViewerModal
                        documents={[currentFile]}
                        baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                    />
                    {/* Previous versions — shown in accordion for internal users only */}
                    {isInternal && previousFiles.length > 0 && (
                        <PreviousFilesAccordion files={previousFiles} />
                    )}
                </>
            )}
            </div>
        </div>
    );
}

// Accordion component for previous file versions
function PreviousFilesAccordion({ files }: { files: any[] }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    return (
        <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden">
            <button
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-2 bg-gray-50 hover:bg-gray-100 transition-colors text-sm text-gray-600 font-medium"
            >
                <span>{files.length} {t("previous_version", { count: files.length }) || (files.length > 1 ? "previous versions" : "previous version")}</span>
                <svg
                    className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {open && (
                <div className="bg-white px-4 py-3 space-y-2 border-t border-gray-200">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">{t("PreviousVersions") || "Previous Versions"}</p>
                    {files.map((doc: any) => (
                        <div key={doc.document_uuid} className="border border-gray-100 rounded-lg p-2 bg-gray-50">
                            <p className="text-xs text-gray-400 mb-1">
                                {doc.created_at ? formatDay(doc.created_at).rest : ""}
                            </p>
                            <DocumentViewerModal
                                documents={[doc]}
                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function LegalAdvisoryDetail({ request }: { request: any }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [publicFeedback, setPublicFeedback] = useState(false);
    const [uploadAmend, setUploadAmend] = useState(false);
    const [auditDraft, setAuditDraft] = useState(false);
    const [showDraftHistory, setShowDraftHistory] = useState(false);
    const [viewTeam, setViewTeam] = useState(false);

    const [edit, setEdit] = useState(false);
    const [formState, setFormState] = useState({ ...request });
    const contextData = useAuth();
    const { showToast } = useToast();

    const [isRespondBackFormOpen, setIsRespondBackFormOpen] = useState(false);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
    const [confirmationAction, setConfirmationAction] = useState<string>('');

    const handleFileChange = (
        file: any,
        fieldName: keyof Pick<
            typeof formState,
            "audit_summary_file"
        >
    ) => {
        setFormState((prev: any) => ({
            ...prev,
            [fieldName]: file,
        }));
    };

    const handleChange = (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
    ) => {
        const { name, value } = e.target;
        setFormState((prev: any) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleRespondBackClick = () => {
        setIsRespondBackFormOpen(!isRespondBackFormOpen);
    }

    const handleAdditionalInfoApprove = async () => {
        setLoading(true);
        const data = new FormData();

        data.append("forward_status", SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_registrar.value);

        try {
            const response = await updateSectorDirective(formState.id, data, contextData.token || contextData?.user?.access_token || "");

            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast(t("ProposalApprovedSuccess") || "Legal research proposal Approved!", "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateApproval") || "Failed to update legal research proposal Approval.", "error");
        } finally {
            setLoading(false);
        }
    }

    const handleReject = () => {
    }

    const handleForward = async (froward_status: string) => {
        const data = new FormData();
        if (formState.status === "Under review" && froward_status === SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_head.value) {
            showToast(t("CannotForwardBeforeEditing") || "Can not forward before editing.", "error");
            return;
        }
        if (formState.status === "Back to Head" && froward_status === SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_director.value) {
            showToast(t("CannotForwardBeforeEditing") || "Can not forward before editing.", "error");
            return;
        }

        if (formState.status === "Back to Director" && froward_status === SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_coordinator.value) {
            showToast(t("CannotForwardBeforeEditing") || "Can not forward before editing.", "error");
            return;
        }

        data.append(
            "forward_status",
            froward_status
        );

        try {
            const response = await updateSectorDirective(formState.id, data, contextData.token || contextData?.user?.access_token || "");

            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast(t("LegislativeDraftForwardedSuccess") || "Legislative Draft Forwarded successfully!", "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateLegislativeDraftRequest") || "Failed to update Legislative Draft request.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleAudit = async () => {
        const data = new FormData();
        data.append("audit_summary", formState.audit_summary);
        data.append("audit_summary_file", formState.audit_summary_file);
        data.append("status", SECTOR_DIRECTIVES_REQUEST_STATUS.audit_submitted.value);

        try {
            const response = await updateSectorDirective(formState.id, data, contextData.token || contextData?.user?.access_token || "");

            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast(t("LegislativeDraftForwardedSuccess") || "Legislative Draft Forwarded successfully!", "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateLegislativeDraftRequest") || "Failed to update Legislative Draft request.", "error");
        } finally {
            setLoading(false);
            setAuditDraft(false);
        }
    };

    const handlePublish = async () => {
        const data = new FormData();
        data.append("status", SECTOR_DIRECTIVES_REQUEST_STATUS.published.value);
        data.append("forward_status", SECTOR_DIRECTIVES_REQUEST_STATUS.not_forwarded.value);

        try {
            const response = await updateSectorDirective(formState.id, data, contextData.token || contextData?.user?.access_token || "");

            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast(t("LegislativeDraftForwardedSuccess") || "Legislative Draft Forwarded successfully!", "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateLegislativeDraftRequest") || "Failed to update Legislative Draft request.", "error");
        } finally {
            setLoading(false);
            setAuditDraft(false);
        }
    };

    const handleDraftApprove = async () => {
        const data = new FormData();
        data.append("status", SECTOR_DIRECTIVES_REQUEST_STATUS.draft_approved.value);
        data.append("forward_status", SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_registrar.value);

        try {
            const response = await updateSectorDirective(formState.id, data, contextData.token || contextData?.user?.access_token || "");

            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast(t("LegislativeDraftForwardedSuccess") || "Legislative Draft Forwarded successfully!", "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateLegislativeDraftRequest") || "Failed to update Legislative Draft request.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleFinalApprove = async () => {
        const data = new FormData();
        data.append("status", SECTOR_DIRECTIVES_REQUEST_STATUS.final_approval.value);

        try {
            const response = await updateSectorDirective(formState.id, data, contextData.token || contextData?.user?.access_token || "");

            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast(t("LegislativeDraftForwardedSuccess") || "Legislative Draft Forwarded successfully!", "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateLegislativeDraftRequest") || "Failed to update Legislative Draft request.", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { console.log("formState: ", formState) }, [formState]);

    return (
        <div className="flex-1 bg-gray-100 mx-4 rounded-lg overflow-y-scroll scrollbar-hide">
            <div className="flex flex-col max-w-4xl bg-white rounded-lg shadow-md p-6">
                {/* Header */}
                <div className="flex items-center justify-between pb-4 mb-3">
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={() => navigate("/sector-directives")}
                            className="text-gray-600 hover:text-gray-800"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-6 w-6"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 19l-7-7 7-7"
                                />
                            </svg>
                        </button>
                    </div>
                    <h1 className="text-2xl font-semibold text-gray-800 ml-8">
                        {t("RequestForCheckingAccuracyOfSectorDirectives") || "Request for Accuracy of Sectors"}
                    </h1>
                    <div className="ml-auto">
                        <StatusChip status={formState.status} />
                        <StatusChip status={formState.forward_status} />
                    </div>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.registrar_forward_to_coordinator.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="ml-auto flex gap-4">
                            <button onClick={() => { setConfirmationAction(SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_coordinator.value); setIsConfirmationModalOpen(true); }} type="button" className="bg-[#0A1D39] text-white rounded-lg px-4">{t("Forward") || "Forward"}</button>
                        </div>
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.stakeholder_forward_to_head.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="flex ml-auto gap-4">
                            <div onClick={() => navigate("/sector-directives/request", { state: { request: formState } })} className="px-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                                <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z" fill="black" />
                                </svg>
                            </div>
                            <div className="flex gap-4">
                                <button onClick={() => { setConfirmationAction(SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_head.value); setIsConfirmationModalOpen(true); }} type="button" className="bg-[#0A1D39] text-white rounded-lg px-4">{t("Forward") || "Forward"}</button>
                            </div>
                        </div>
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.head_forward_to_director.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="flex ml-auto gap-4">
                            <ShowUI
                                ui_key={LEGISLATIVE_DRAFTING_UI_RULES.edit_head_forward_to_director.value}
                                currentStatus={formState.status}
                                forwardStatus={formState.forward_status}
                            >
                                <div onClick={() => setEdit(!edit)} className="px-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                                    {edit ?
                                        <p>{t("Cancel") || "Cancel"}</p> :
                                        <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z" fill="black" />
                                        </svg>}
                                </div>
                            </ShowUI>
                            <div className="flex gap-4">
                                <button onClick={() => { setConfirmationAction(formState.status === SECTOR_DIRECTIVES_REQUEST_STATUS.under_review.value ? SECTOR_DIRECTIVES_REQUEST_STATUS.not_forwarded.value : SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_director.value); setIsConfirmationModalOpen(true); }} type="button" className="bg-[#0A1D39] text-white rounded-lg px-4">{t("Forward") || "Forward"}</button>
                            </div>
                        </div>
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.director_forward_to_coordinator.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="flex ml-auto gap-4">
                            <div onClick={() => setEdit(!edit)} className="px-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                                {edit ?
                                    <p>{t("Cancel") || "Cancel"}</p> :
                                    <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z" fill="black" />
                                    </svg>}
                            </div>
                            <div className="flex gap-4">
                                <button onClick={() => { setConfirmationAction(formState.status === SECTOR_DIRECTIVES_REQUEST_STATUS.back_to_head.value ? SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_head.value : SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_coordinator.value); setIsConfirmationModalOpen(true); }} type="button" className="bg-[#0A1D39] text-white rounded-lg px-4">{t("Forward") || "Forward"}</button>
                            </div>
                        </div>
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.coordinator_forward_to_prosecutor.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="flex ml-auto gap-4">
                            <ShowUI
                                ui_key={LEGISLATIVE_DRAFTING_UI_RULES.edit_coordinator_forward_to_prosecutor.value}
                                currentStatus={formState.status}
                                forwardStatus={formState.forward_status}
                            >
                                <div onClick={() => setEdit(!edit)} className="px-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                                    {edit ?
                                        <p>{t("Cancel") || "Cancel"}</p> :
                                        <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z" fill="black" />
                                        </svg>}
                                </div>
                            </ShowUI>
                            <div className="flex gap-4">
                                <button onClick={() => { setConfirmationAction(formState.status === SECTOR_DIRECTIVES_REQUEST_STATUS.back_to_director.value ? SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_coordinator.value : SECTOR_DIRECTIVES_REQUEST_STATUS.assigned_to_prosecutor.value); setIsConfirmationModalOpen(true); }} type="button" className="bg-[#0A1D39] text-white rounded-lg px-4">{t("Forward") || "Forward"}</button>
                            </div>
                        </div>
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.coordinator_forward_to_head.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="flex ml-auto gap-4">
                            <div className="flex gap-4">
                                <button onClick={() => { setConfirmationAction(SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_head.value); setIsConfirmationModalOpen(true); }}
                                    type="button" className="bg-[#0A1D39] text-white rounded-lg px-4">{t("Forward") || "Forward"}</button>
                            </div>
                        </div>
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.additional_info_action.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="flex ml-auto gap-4">
                            <div className="flex gap-4">
                                <button onClick={() => { setConfirmationAction(SECTOR_DIRECTIVES_REQUEST_STATUS.not_forwarded.value); setIsConfirmationModalOpen(true); }} type="button" className="bg-[#0A1D39] text-white rounded-lg px-4">{t("Forward") || "Forward"}</button>
                            </div>
                        </div>
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.prosecutor_forward_to_coordinator.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="flex ml-auto gap-4">
                            <div className="flex gap-4">
                                <button onClick={() => { setConfirmationAction(SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_coordinator.value); setIsConfirmationModalOpen(true); }}
                                    type="button" className="bg-[#0A1D39] text-white rounded-lg px-4">{t("Forward") || "Forward"}</button>
                            </div>
                        </div>
                    </ShowUI>
                    <div>
                    </div>
                </div>
                {/* Broadcast to Public Website — hidden for the director */}
                {!contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector") &&
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.publish.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="flex mb-4">
                            <button onClick={handlePublish} disabled={loading} className="p-4 flex-1 bg-[#0A1D39] text-white rounded-lg disabled:opacity-70">
                                {loading ? (
                                    <div className="flex items-center justify-center gap-2">
                                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                                        </svg>
                                        {t("Submitting") || "Submitting..."}
                                    </div>
                                ) : t("BroadcastToPublicWebsite") || "Brodcast to Public Website"}
                            </button>
                        </div>
                    </ShowUI>
                }
                <ShowUI
                    ui_key={LEGISLATIVE_DRAFTING_UI_RULES.broadcasted.value}
                    currentStatus={formState.status}
                    forwardStatus={formState.forward_status}
                >
                    <div className="flex mb-4">
                        <button className="p-4 flex-1 bg-[#0A1D39] text-white rounded-lg">{t("broadcasted") || "Brodcasted"}</button>
                    </div>
                </ShowUI>
                {/* Show Summary Written for internal users always, and for external users only when archived + forwarded_to_director */}
                {(contextData?.user?.user_type === "internal" ||
                    (formState?.status === "archived" && formState?.forward_status === "forwarded_to_director")) && (
                    <SummaryWrittenCard formState={formState} setFormState={setFormState} />
                )}
                {/* Accuracy Completion Letter — visible to all users */}
                {formState?.uploaded_files?.find((document: any) => document.document_type === "accurecy_completion_letter_file") && (
                    <div className="flex-1 bg-white mb-4 p-4 rounded-lg border border-gray-200 shadow-sm">
                        <p className="font-semibold text-base text-[#0A1D39] mb-3 pb-2 border-b border-gray-200">{t("CompletionLetter") || "Completion Letter"}</p>
                        <DocumentViewerModal
                            documents={formState?.uploaded_files.filter((document: any) => document.document_type === "accurecy_completion_letter_file")}
                            baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                        />
                    </div>
                )}
                <ShowUI
                    ui_key={LEGISLATIVE_DRAFTING_UI_RULES.final_approval.value}
                    currentStatus={formState.status}
                    forwardStatus={formState.forward_status}
                >
                    <LegislativeRespondBackForm
                        request={formState}
                        setLegalResearch={setFormState}
                        isOpen={isRespondBackFormOpen}
                        onClose={() => setIsRespondBackFormOpen(false)}
                        forward={SECTOR_DIRECTIVES_REQUEST_STATUS.audit_responded.value}
                        type="sector"
                    />
                    <div className="flex gap-4">
                        <button onClick={handleRespondBackClick} className="p-4 flex-1 bg-gray-200 text-gray-800 rounded-lg">{t("RespondBack") || "Respond Back"}</button>
                        <button onClick={handleFinalApprove} disabled={loading} className="p-4 flex-1 bg-[#0A1D39] text-white rounded-lg disabled:opacity-70">
                            {loading ? (
                                <div className="flex items-center justify-center gap-2">
                                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
                                    </svg>
                                    {t("Submitting") || "Submitting..."}
                                </div>
                            ) : t("FinalApproveDraftLaw") || "Final Approve Draft Law"}
                        </button>
                    </div>
                </ShowUI>
                <div className="flex-1 overflow-y-scroll scrollbar-hide">
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.forward_darft.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <LegislativeRespondBackForm
                            request={formState}
                            setLegalResearch={setFormState}
                            isOpen={isRespondBackFormOpen}
                            onClose={() => setIsRespondBackFormOpen(false)}
                            forward={SECTOR_DIRECTIVES_REQUEST_STATUS.draft_responded.value}
                            type="sector"
                        />
                        <div className="mt-4 pb-8 border-b border-gray-200 flex flex-col sm:flex-row justify-between gap-3">
                            <button
                                type="button"
                                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-gray-200 text-gray-800 font-semibold hover:bg-gray-300 transition-colors"
                                onClick={handleRespondBackClick}
                            >
                                {loading ? (
                                    <>{LoadingIcon} {t("Submitting") || "Submitting..."}</>
                                ) : (
                                    <>
                                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                        </svg>
                                        {t("RespondBack") || "Respond back"}
                                    </>
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={() => { setConfirmationAction(SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_director.value); setIsConfirmationModalOpen(true); }}
                                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#0A1D39] text-white font-semibold hover:bg-[#13294d] transition-colors"
                            >
                                {loading ? (
                                    <>{LoadingIcon} {t("Submitting") || "Submitting..."}</>
                                ) : (
                                    <>
                                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                        {t("ApproveAndForwardToDirector") || "Approve and Forward to Director"}
                                    </>
                                )}
                            </button>
                        </div>
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.forward_darft_to_director.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <LegislativeRespondBackForm
                            request={formState}
                            setLegalResearch={setFormState}
                            isOpen={isRespondBackFormOpen}
                            onClose={() => setIsRespondBackFormOpen(false)}
                            forward={SECTOR_DIRECTIVES_REQUEST_STATUS.draft_responded.value}
                            type="sector"
                        />
                        <div className="mt-4 pb-8 border-b border-gray-200 flex flex-col sm:flex-row justify-between gap-3">
                            <button
                                type="button"
                                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-gray-200 text-gray-800 font-semibold hover:bg-gray-300 transition-colors"
                                onClick={handleRespondBackClick}
                            >
                                {loading ? (
                                    <>{LoadingIcon} {t("Submitting") || "Submitting..."}</>
                                ) : (
                                    <>
                                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                        </svg>
                                        {t("RespondBack") || "Respond back"}
                                    </>
                                )}
                            </button>
                            {(() => {
                                const isCoordinator = contextData?.user?.roles?.some((p: string) => p === "LegalResearchCoordinator");
                                const target = isCoordinator
                                    ? SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_director.value
                                    : SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_head.value;
                                const label = isCoordinator
                                    ? (t("ApproveAndForwardToDirector") || "Approve and Forward to Director")
                                    : (t("ApproveAndForwardToHead") || "Approve and Forward to Head");
                                return (
                                    <button
                                        type="button"
                                        onClick={() => { setConfirmationAction(target); setIsConfirmationModalOpen(true); }}
                                        className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#0A1D39] text-white font-semibold hover:bg-[#13294d] transition-colors"
                                    >
                                        {loading ? (
                                            <>{LoadingIcon} {t("Submitting") || "Submitting..."}</>
                                        ) : (
                                            <>
                                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                </svg>
                                                {label}
                                            </>
                                        )}
                                    </button>
                                );
                            })()}
                        </div>
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.forward_darft_to_head.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <LegislativeRespondBackForm
                            request={formState}
                            setLegalResearch={setFormState}
                            isOpen={isRespondBackFormOpen}
                            onClose={() => setIsRespondBackFormOpen(false)}
                            forward={SECTOR_DIRECTIVES_REQUEST_STATUS.draft_responded.value}
                            type="sector"
                        />

                        <div className="mt-4 pb-8 border-b border-gray-200 flex flex-col sm:flex-row justify-between gap-3">
                            <button
                                type="button"
                                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-gray-200 text-gray-800 font-semibold hover:bg-gray-300 transition-colors"
                                onClick={handleRespondBackClick}
                            >
                                {loading ? (
                                    <>{LoadingIcon} {t("Submitting") || "Submitting..."}</>
                                ) : (
                                    <>
                                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                                        </svg>
                                        {t("RespondBack") || "Respond back"}
                                    </>
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={() => { setConfirmationAction('draft_approve'); setIsConfirmationModalOpen(true); }}
                                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#0A1D39] text-white font-semibold hover:bg-[#13294d] transition-colors"
                            >
                                {loading ? (
                                    <>{LoadingIcon} {t("Submitting") || "Submitting..."}</>
                                ) : (
                                    <>
                                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                        {t("Approve") || "Approve"}
                                    </>
                                )}
                            </button>
                        </div>
                    </ShowUI>
                    {/* Documents */}
                    <div className="mt-6 space-y-4 border border-gray-400 p-4 mb-8 rounded-lg">
                        {formState?.uploaded_files?.find((document: any) => document.document_type === "directive_file") &&
                            <>
                                {formState?.registry_number &&
                                    <p className="text-[#432101AB] font-normal text-base">{t("RegNumber") || "Register Number"}: {formState.registry_number}</p>
                                }
                                <p className="text-[#432101AB] font-normal text-base">{t("RegisterLetterNumber") || "Register Letter Number"}: {formState?.request_letter_reference_number}</p>
                                <DocumentViewerModal
                                    documents={formState?.uploaded_files.filter((document: any) => document.document_type === "directive_file")}
                                    baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                                />
                            </>
                        }
                        {formState?.uploaded_files?.find((document: any) => document.document_type === "che_accuracy_request_letter_file") &&
                            <DocumentViewerModal
                                documents={formState?.uploaded_files.filter((document: any) => document.document_type === "che_accuracy_request_letter_file")}
                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                            />
                        }
                        <div className="mb-8">
                            <p className="text-[#432101AB] font-normal text-base">{t("ExplanatoryNote") || "Explanatory Note"}</p>
                            <p className="text-gray-500 font-medium mb-2">
                                {formState?.explanatory_notes}
                            </p>
                        </div>
                    </div>
                    {contextData?.user?.user_type === "internal" &&
                        formState?.prosecutor_assignments &&
                        formState?.prosecutor_assignments.length !== 0 &&
                        (<div className="mt-6 mb-6 flex bg-gray-50 rounded-lg border border-gray-200">
                            <ActionAvatars
                                title={t("TeamList") || "Team List"}
                                users={formState?.prosecutor_assignments}
                                prosecutor={true}
                            />
                            {contextData?.user?.roles?.some((p) => p === "LegalResearchCoordinator") && (!edit ?
                                <svg onClick={() => setEdit(true)} className="my-auto mr-2 hover:bg-gray-300 rounded-lg cursor-pointer" width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z" fill="black" />
                                </svg> : <p onClick={() => setEdit(false)} className="my-auto mr-2 hover:bg-gray-300 rounded-lg px-4 cursor-pointer">{t("Cancel") || "Cancel"}</p>)}
                            <svg onClick={() => setViewTeam(!viewTeam)} className="mr-2 ml-auto my-auto cursor-pointer" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <mask id="mask0_9_31087" maskUnits="userSpaceOnUse" x="1" y="5" width="22" height="14">
                                    <path d="M12 18C17.523 18 22 12 22 12C22 12 17.523 6 12 6C6.477 6 2 12 2 12C2 12 6.477 18 12 18Z" fill="white" stroke="white" stroke-width="1.33333" stroke-linejoin="round" />
                                    <path d="M12 14.5C12.663 14.5 13.2989 14.2366 13.7678 13.7678C14.2366 13.2989 14.5 12.663 14.5 12C14.5 11.337 14.2366 10.7011 13.7678 10.2322C13.2989 9.76339 12.663 9.5 12 9.5C11.337 9.5 10.7011 9.76339 10.2322 10.2322C9.76339 10.7011 9.5 11.337 9.5 12C9.5 12.663 9.76339 13.2989 10.2322 13.7678C10.7011 14.2366 11.337 14.5 12 14.5Z" fill="black" stroke="black" stroke-width="1.33333" stroke-linejoin="round" />
                                </mask>
                                <g mask="url(#mask0_9_31087)">
                                    <path d="M0 0H24V24H0V0Z" fill="#EFC83D" />
                                </g>
                            </svg>
                        </div>)}
                    {edit &&
                        <ShowUI
                            ui_key={LEGISLATIVE_DRAFTING_UI_RULES.edit_coordinator_forward_to_prosecutor.value}
                            currentStatus={"all"}
                            forwardStatus={"all"}
                        >
                            <CoordinatorRemark type="sector_directives" setEdit={setEdit} setFormState={setFormState} formState={formState} />
                        </ShowUI>
                    }
                    <br />
                    {/* Request for Additional Info / Summary Written Comments — above the Additional Information Request card */}
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.draft_and_additional_file.value}
                        currentStatus={"all"}
                        forwardStatus={formState.forward_status}
                    >
                        <ProsecutorRemark setFormState={setFormState} formState={formState} />
                    </ShowUI>
                    {!!formState.additional_infos_accurecy?.[0] &&
                        <div className="flex-1 bg-white mb-4 p-4 rounded-lg border border-gray-400">
                            <div className="pb-2 border-b border-gray-200 mb-4">
                                <p>{t("AdditionalInformationRequest") || "Additional Information Request"}</p>
                            </div>
                            <p className="text-[#747171] font-normal text-xs mb-3">{formatDay(formState?.additional_infos_accurecy[0].requested_at).rest}</p>
                            <p className="font-regular text-base text-[#606060]">{formState?.additional_infos_accurecy[0].request_summary}</p>
                            {formState?.additional_infos_accurecy[0].uploaded_files?.find((document: any) => document.document_type === "accur_additional_request_file") &&
                                <DocumentViewerModal
                                    documents={formState?.additional_infos_accurecy[0].uploaded_files.filter((document: any) => document.document_type === "accur_additional_request_file")}
                                    baseUrl={`${process.env.REACT_APP_API_BASE_URL}/api/accuracy_government_sector/directive-additional-info-files`}
                                />
                            }
                            <p>{t("AdditionalInformationResponse") || "Additional Information Response"}</p>
                            <p className="text-[#747171] font-normal text-xs mb-3">{formatDay(formState?.additional_infos_accurecy[0].responded_at).rest}</p>
                            <p className="font-regular w-[55ch] text-justify text-base text-[#606060]">{formState?.additional_infos_accurecy[0].response_note}</p>
                            {!!formState.additional_infos_accurecy[0]?.uploaded_files.find((document: any) => document.document_type === "accur_additional_response_file")
                                && !(formState.current_status === "Waiting for Additional Information") &&
                                <div>
                                    {formState?.additional_infos_accurecy[0].uploaded_files?.find((document: any) => document.document_type === "accur_additional_response_file") &&
                                        <DocumentViewerModal
                                            documents={formState?.additional_infos_accurecy[0].uploaded_files.filter((document: any) => document.document_type === "accur_additional_response_file")}
                                            baseUrl={`${process.env.REACT_APP_API_BASE_URL}/api/accuracy_government_sector/directive-additional-info-files`}
                                        />
                                    }
                                </div>
                            }
                        </div>
                    }
                    {/* Additional Details */}
                    {formState.requested_by !== "internal" &&
                        contextData?.user?.user_type === "internal" &&
                        <>
                            <div>
                                <div className="flex">
                                    <p className="text-[#432101AB] font-normal text-base">{t("StakeholderName") || "Stakeholder"}</p>
                                    <p className="ml-auto text-[#0A1D39] font-semibold text-base">{formState?.organization_name}</p>
                                </div>
                                <hr className="w-full border-t-2 border-gray-300 my-4" />
                                <div className="flex">
                                    <p className="text-[#432101AB] font-normal text-base">{t("StakeholderType") || "Stakeholder Type"}</p>
                                    <p className="ml-auto text-[#0A1D39] font-semibold text-base">{formState?.organization_type}</p>
                                </div>
                                <hr className="w-full border-t-2 border-gray-300 my-4" />
                                <div className="flex">
                                    <p className="text-[#432101AB] font-normal text-base">{t("AppliedDate") || "Applied Date"}</p>
                                    <p className="ml-auto text-[#0A1D39] font-semibold text-base">{formatDate(formState?.created_at)}</p>
                                </div>
                                <hr className="w-full border-t-2 border-gray-300 my-4" />
                                <div className="flex">
                                    <p className="text-[#432101AB] font-normal text-base">{t("Address") || "Address"}</p>
                                    <p className="ml-auto text-[#0A1D39] font-semibold text-base">{formState?.organization_state}, {formState?.organization_zone}, {formState?.organization_kebele}</p>
                                </div>
                                <hr className="w-full border-t-2 border-gray-300 my-4" />
                                <div className="flex">
                                    <p className="text-[#432101AB] font-normal text-base">{t("Email") || "Email"}</p>
                                    <p className="ml-auto text-[#0A1D39] font-semibold text-base">{formState?.email_user_organization}</p>
                                </div>
                                <hr className="w-full border-t-2 border-gray-300 my-4" />
                            </div>
                            <br />
                        </>}


                    {/* Remark Section */}
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.internal_ui.value}
                        currentStatus={"all"}
                        forwardStatus={"all"}
                    >
                        {formState.remarks && formState?.remarks?.find((remark: any) => remark.role === "head") &&
                            <div>
                                <p className="text-[#432101AB] font-normal text-base">{t("RemarkForDirector") || "Remark For Director"}</p>
                                <p className="text-gray-500 font-medium mb-2">
                                    {formState?.remarks.filter((remark: any) => remark.role === "head").slice().reverse()[0]?.remark}
                                </p>
                                <br />
                            </div>
                        }
                        {formState.remarks && formState?.remarks?.find((remark: any) => remark.role === "director") &&
                            <div>
                                <p className="text-[#432101AB] font-normal text-base">{t("RemarkForCoordinator") || "Remark For Coordinator"}</p>
                                <p className="text-gray-500 font-medium mb-2">
                                    {formState?.remarks.filter((remark: any) => remark.role === "director").slice().reverse()[0]?.remark}
                                </p>
                                <br />
                            </div>
                        }
                        {formState.remarks && formState?.remarks?.find((remark: any) => remark.role === "coordinator") &&
                            <div>
                                <p className="text-[#432101AB] font-normal text-base">{t("RemarkForProsecutor") || "Remark For Prosecutor"}</p>
                                <p className="text-gray-500 font-medium mb-2">
                                    {formState?.remarks.filter((remark: any) => remark.role === "coordinator").slice().reverse()[0]?.remark}
                                </p>
                                <br />
                            </div>
                        }
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.edit_head_forward_to_director.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <HeadRemark type="sector_directives" setEdit={setEdit} setFormState={setFormState} formState={formState} />
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.edit_director_forward_to_coordinator.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <DirectorRemark type="sector_directives" setEdit={setEdit} setFormState={setFormState} formState={formState} />
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.edit_coordinator_forward_to_prosecutor.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <CoordinatorRemark type="sector_directives" setEdit={setEdit} setFormState={setFormState} formState={formState} />
                    </ShowUI>
                </div>
            </div>
            {publicFeedback &&
                (<PublicFeedback
                    data={formState}
                    setLegalResearch={setFormState}
                    isOpen={publicFeedback}
                    onClose={() => setPublicFeedback(false)}
                />)
            }
            {uploadAmend &&
                (<UploadAmendedFile
                    data={formState}
                    setLegalResearch={setFormState}
                    isOpen={uploadAmend}
                    onClose={() => setUploadAmend(false)}
                />)
            }
            {viewTeam &&
                <TeamView teamMembers={formState.prosecutor_assignments} onClose={() => setViewTeam(false)} />}
            {isConfirmationModalOpen && (
                <ConfirmationModal
                    isOpen={isConfirmationModalOpen}
                    isLoading={loading}
                    onCancel={() => setIsConfirmationModalOpen(false)}
                    onConfirm={() => {
                        if (confirmationAction === 'draft_approve') {
                            handleDraftApprove();
                        } else {
                            handleForward(confirmationAction);
                        }
                        setIsConfirmationModalOpen(false);
                    }}
                    title={t("ConfirmForward") || "Confirm Forward"}
                    message={t("ConfirmForwardMessage") || "Are you sure you want to forward this request? This action cannot be undone."}
                    confirmText={t("Forward") || "Forward"}
                    cancelText={t("Cancel") || "Cancel"}
                    type="success"
                />
            )}
        </div>
    );
}

export default LegalAdvisoryDetail;